// ACI design example:
// chrome-extension://efaidnbmnnnibpcajpcglclefindmkaj/https://structurepoint.org/publication/pdf/Reinforced-Concrete-Square-Spread-Footing-Analysis-Design-ACI318-14.pdf
// chrome-extension://efaidnbmnnnibpcajpcglclefindmkaj/https://www.concrete.org/portals/0/files/pdf/318-example-1_rf_r1.pdf
import FootingConditions from './models/footing-conditions.js'
import Geometry from './models/geometry.js'
import Load from './models/load.js'
import Steel from './models/steel.js'

const rebarProperties = {
  3: {a: 0.110, d: 0.375},
  4: {a: 0.200, d: 0.500},
  5: {a: 0.310, d: 0.625},
  6: {a: 0.440, d: 0.750},
  7: {a: 0.600, d: 0.875},
  8: {a: 0.790, d: 1.000},
  9: {a: 1.000, d: 1.128},
  10: {a: 1.270, d: 1.270},
  11: {a: 1.560, d: 1.410},
  14: {a: 2.250, d: 1.693},
  18: {a: 4.000,  d: 2.257}
}

export default class FootingDesign {
  footingConditions = new FootingConditions()
  geometry = new Geometry()
  load = new Load()
  steel = new Steel()
  createdAt = Date.now()
  modifiedAt = Date.now()
  title = 'New Footing Design'
  key = null
  isActive = false

  footingPressureOk = true
  oneWayShearOk = true
  twoWayShearOk = true
  flexureOk = true
  barDevelopementLengthOk = true
  maxBearingLoadOk = true
  allowableBearingLoadOk = true

  serialize() {
    this.save()
    return {
      'key': this.key,
      'title': this.title,
      'isActive': this.isActive,
      'createdAt': this.createdAt,
      'modifiedAt': this.modifiedAt,
      'footingConditions': this.footingConditions.serialize(),
      'load': this.load.serialize(),
      'steel': this.steel.serialize(),
      'geometry': this.geometry.serialize()
    }
  } 

  save() {
    this.footingConditions.dirty = false
    this.load.dirty = false
    this.steel.dirty = false
    this.geometry.dirty = false
  }

  deserialize(json) {
    const d = json //JSON.parse(jsonString)
    this.key = d.key
    this.title = d.title
    this.isActive = d.isActive
    this.createdAt = d.createdAt
    this.modifiedAt = d.modifiedAt
    this.footingConditions.deserialize(d.footingConditions)
    this.load.deserialize(d.load)
    this.steel.deserialize(d.steel)
    this.geometry.deserialize(d.geometry)
  }

  getRebarProperties() {
    return rebarProperties[this.steel.rebarSize]
  }

  isDesignOk() {
    //console.log(this.footingPressureOk)
    //console.log(this.footingPressureOk)
    //console.log(this.footingPressureOk && this.oneWayShearOk && this.twoWayShearOk &&
    //  this.flexureOk && this.barDevelopementLengthOk && this.maxBearingLoadOk && this.allowableBearingLoadOk)

    //return false
    return (this.footingPressureOk && this.oneWayShearOk && this.twoWayShearOk &&
    this.flexureOk && this.barDevelopementLengthOk && this.maxBearingLoadOk && this.allowableBearingLoadOk)
  }

  configureCard(checkId, cardId, checkSucceded) {
    if(checkSucceded) {
      document.getElementById(checkId).innerHTML = '<i class="fa fa-check fs-5"></i>'
      document.getElementById(cardId).classList.remove('card-danger')
      document.getElementById(cardId).classList.add('card-success')
    } else {
      document.getElementById(checkId).innerHTML = '<i class="fa fa-close fs-5"></i>'
      document.getElementById(cardId).classList.add('card-danger')
      document.getElementById(cardId).classList.remove('card-success')
    }
  }

  calculateLoadCombinations() {
    const format = (num, decimals) => num.toLocaleString('en-US', {
      minimumFractionDigits: 2,      
      maximumFractionDigits: 2,
    })

    const P1 = format(1.4 * this.load.columnDeadLoad)
    const P2 = format(1.2 * this.load.columnDeadLoad + 1.6 * this.load.columnLiveLoad)

    this.P_u = Math.max(P1, P2) 

    this.phi_v = this.footingConditions.shearStrengthReductionFactor
    this.phi_f = this.footingConditions.flexureStrengthReductionFactor

    this.loadCombinationsStr = `
    <div><em>P<sub>1</sub> = 1.4 D</em> = ${P1} kips</div>
    <div><em>P<sub>2</sub> = 1.2 D + 1.6 L</em> = ${P2} kips</div>
    <div><em>P<sub>u</sub> = max(P<sub>1</sub>, P<sub>2</sub>)</em> = ${this.P_u} kips</div>
    <div><em>&phi;<sub>v</sub></em> = ${this.phi_v} (shear strength reduction factor) <span class='float-end fw-light'>ACI 318-19 21.2.1</span></div>
    <div><em>&phi;<sub>f</sub></em> = ${this.phi_f} (flexure strength reduction factor) <span class='float-end fw-light'>ACI 318-19 21.2.1</span></div>
      `

    //document.getElementById('load-combindations').innerHTML = 

  }

  showLoadCombinations() {
    document.getElementById('load-combinations').innerHTML = this.loadCombinationsStr
  }

  calculateFootingPressure() {
    const h = this.geometry.footingThickness
    const P_u = this.P_u
    const b_w = this.geometry.footingWidth
    const q_allowable = this.footingConditions.soilBearing
    
    const q_footing = h / 12 * this.footingConditions.concreteDensity
    const q_fill = this.load.fillDepth / 12 * this.load.fillDensity
    const q_floorLoad = this.load.floorLoad
    const q_floor = this.load.floorSlabThickness / 12 * this.load.floorSlabDensity
    const q_n = q_allowable - q_footing - q_fill - q_floor - q_floorLoad // net soil pressure
    const A_g_required = Math.round((this.load.columnDeadLoad * 1000 + this.load.columnLiveLoad * 1000) / q_n)
    const b_w_required = Math.sqrt(A_g_required) * 12
    const b_wOk = b_w * 1.01 > b_w_required // 1.01 to allow for some tolerance based on rounding to nearest inch
    const q_nu = P_u / Math.pow(b_w / 12, 2) * 1000 // psf factored net soil pressure
    this.q_nu = q_nu

    this.footingPressureOk = b_wOk

    this.footingPressureStr = `
    <div>Footing thickness: <em>h</em> = ${h} inches</div>
    <div>Footing width: <em>b<sub>w</sub></em> = ${b_w} inches</div>
    <div><em>q<sub>allowable</sub></em> = ${q_allowable} psi</div>
    <div>Net Soil Pressure: <em>q<sub>n</sub> = q<sub>allowable</sub> - q<sub>footing</sub> - q<sub>fill</sub> - q<sub>floor weight</sub> - q<sub>floor load</sub></em> = ${q_n.toFixed(2)} psf</div>
    <div>Required Footing Area: <em>A<sub>g, required</sub> = P<sub>service</sub> / q<sub>n</sub></em> = (${this.load.columnDeadLoad} kips + ${this.load.columnLiveLoad} kips) / ${q_n.toFixed(2)} = ${A_g_required} ft<sup>2</sup></div>
    <div>Required Footing Width: <em>b<sub>w, required</sub><em> = &radic;<span style="text-decoration:overline">${A_g_required} ft<sup>2</sup></span> &asymp; ${Math.floor(b_w_required)} in</div>
    <div>Factored Net Soil Pressure: <em>q<sub>n,u</sub> = P<sub>u</sub> / A<sub>g<sub></em> = ${Math.round(q_nu)} psf</div>    
      `
  }

  showFootingPressure() { 
    this.configureCard('footing-pressure-check', 'footing-pressure-card', this.footingPressureOk)
    document.getElementById('footing-pressure').innerHTML = this.footingPressureStr
  }

  calculateOneWayShear() {
    const h = this.geometry.footingThickness
    const b_w = this.geometry.footingWidth
    const d_b = rebarProperties[this.steel.rebarSize].d
    const cover = this.footingConditions.cover
    const d_avg = ((h - cover - d_b / 2) + (h - cover - d_b - d_b / 2)) / 2
    const q_nu = this.q_nu
    const phi = 0.75 // ACI 318-19 22.2.1 - shear reduction factor
    const lambda = 1 // ACI 318-19 (Table 25.4.2.4) normal weight concrete 
    const fc = this.footingConditions.concreteStrength * 1000 // psi

    const onewayTributaryLength = b_w / 2 - this.geometry.columnWidth / 2 - d_avg
    const tributaryShearArea = b_w / 12 * onewayTributaryLength / 12
    const V_u_oneway = q_nu / 1000 * b_w / 12 * onewayTributaryLength / 12
    
    // ACI 318-14 (22.5.5.1)
    const phi_Vc_oneway = phi * 2 * lambda * Math.sqrt(fc) * b_w * d_avg / 1000
    const onewayShearOk =  V_u_oneway < phi_Vc_oneway
    
    this.oneWayShearOk = onewayShearOk

    this.oneWayShearStr = `
    <div>Average Depth: <em>d<sub>avg</sub></em> = ${d_avg} inches <span class='float-end fw-light'>ACI 318-19 22.6.2.1</span></div>
    <div><em>Tributary Section Width = ${Math.round(onewayTributaryLength)} inches</div>
    <div><em>Tributary Shear Area = ${Math.round(tributaryShearArea)} sq ft</div>
    <div><em>V<sub>u</sub> = <em>q<sub>n,u</sub> &times; Tributary Shear Area</em> = ${Math.round(V_u_oneway)} kips</div>
    <div><em>&phi;V<sub>c</sub> = ${Math.round(phi_Vc_oneway)} kips (ACI 318-19 22.6.5.2)</div>
      `
  }

  showOneWayShear() {
    this.configureCard('one-way-shear-check', 'one-way-shear-card', this.oneWayShearOk)
    document.getElementById('one-way-shear').innerHTML = this.oneWayShearStr
  }

  calculateTwoWayShear() {
    const h = this.geometry.footingThickness
    const b_w = this.geometry.footingWidth
    const q_nu = this.q_nu
    
    // ACI 318-19 (22.6.2.1) 
    const fc = this.footingConditions.concreteStrength * 1000 // psi
    const d_b = rebarProperties[this.steel.rebarSize].d
    const cover = this.footingConditions.cover // how much concrete covers the rebar (inches)
    // davg is the average of the nominal depth in both directions (which exlcudes bottom concrete cover and rebar)
    // this dimension defines the Critical Shear Perimeter around the column at approximately 45 deg angle out from the 
    // column on each side
    const d_avg = ((h - cover - d_b / 2) + (h - cover - d_b - d_b / 2)) / 2
    const criticalShearSideLength = d_avg + this.geometry.columnWidth
    const criticalShearArea = Math.pow(criticalShearSideLength, 2)
    const footingArea = Math.pow(b_w, 2)
    const tributaryShearArea = footingArea - criticalShearArea
    const V_u_twoway = q_nu / 1000 * tributaryShearArea / 144 // kips
    const bo = 4 * criticalShearSideLength // criticalShearPerimeter in
    const v_u = V_u_twoway / (bo * d_avg) * 1000 // psi
    
    //ACI 318-14 (22.6.5.2)
    const lambda = 1 // ACI 318-19 (Table 25.4.2.4) normal weight concrete 
    const phi = 0.75 // ACI 318-19 22.2.1 - shear reduction factor
    const beta = 1.0 // ratio of long to short dimension (it's 1 b/c these are square footings)
    const alpha_s = 40 // constant for shear strength: 40 for interior columns, 30 for edge columns and 20 for corner columns
    const phi_vc_a = phi * 4 * lambda * Math.sqrt(fc) 
    const phi_vc_b = phi * (2 + 4 / beta) * lambda * Math.sqrt(fc)
    const phi_vc_c = phi * (2 + alpha_s * d_avg / bo) * lambda * Math.sqrt(fc)
    const phi_vc_twoway = Math.min(Math.min(phi_vc_a, phi_vc_b), phi_vc_c)
    const twowayShearOk = phi_vc_twoway > v_u

    this.twoWayShearOk = twowayShearOk
    
    this.twoWayShearStr = `
    <div>Average Depth: <em>d<sub>avg</sub></em> = ${d_avg} inches <span class='float-end fw-light'>ACI 318-19 22.6.2.1</span></div>
    <div><em>Tributary Shear Area</em> = ${Math.round(tributaryShearArea / 144)} sq ft</div>
    <div><em>V<sub>u</sub> = q<sub>n,u</sub> &times; Tributary Shear Area</em> = ${Math.round(V_u_twoway)} kips</div>
    <div><em>v<sub>u</sub> = V<sub>u</sub> / (b<sub>o</sub> &times; d<sub>avg</sub>)</em> = ${Math.round(v_u)} psi</div>
    <div><em>&phi;v<sub>c</sub></em> = ${Math.round(phi_vc_twoway)} psi <span class='float-end fw-light'>ACI 318-19 22.6.5.2</span></div>
    `

  }

  showTwoWayShear() {
    this.configureCard('two-way-shear-check', 'two-way-shear-card', this.twoWayShearOk)
    document.getElementById('two-way-shear').innerHTML = this.twoWayShearStr
  }

  calculateFlexureStrengthAndAreaOfSteel() {
    const h = this.geometry.footingThickness
    const b_w = this.geometry.footingWidth
    const d_b = rebarProperties[this.steel.rebarSize].d
    const cover = this.footingConditions.cover // how much concrete covers the rebar (inches)
    const d_avg = ((h - cover - d_b / 2) + (h - cover - d_b - d_b / 2)) / 2
    const q_nu = this.q_nu
    const fc = this.footingConditions.concreteStrength * 1000 // psi

    // factored moment
    const fy = this.footingConditions.tensileStrength * 1000 // psi
    const columnEdgeToFootingEdge = (b_w / 2 - this.geometry.columnWidth / 2)
    const M_u = q_nu / 1000 * b_w / 12 * Math.pow(columnEdgeToFootingEdge / 12, 2) / 2
    
    // assume tension controlled phi = 0.9 (ACI 318-19 22.2.1)
    
    const phiMoment = 0.9
    const beta1 = 0.85
    const jd = 0.976 * d_avg
    const As = M_u * 12 * 1000 / (phiMoment * fy * jd)
    const a = As * fy / (beta1 * fc * b_w)
    const c = a / beta1
    const epsilon_t = 0.003 / c * d_avg - 0.003
    const tensionControls = epsilon_t > 0.005
    const As_final = M_u * 12 * 1000 / (phiMoment * fy * (d_avg - a/2))

    //ACI 318-14 (7.6.1.1) 
    const Asmin_a = 0.0018 * 60000 / fy * b_w * h
    const Asmin_b = 0.0014 * b_w * h
    const Asmin_final = Math.max(Asmin_a, Asmin_b)

    const numRebar = Math.floor((b_w - 2 * cover) / this.steel.rebarSpacing) + 1
    const rebarArea = rebarProperties[this.steel.rebarSize].a
    const totalRebarArea = numRebar * rebarArea
    const steelAreaOk = totalRebarArea > Asmin_final

    this.flexureOk = steelAreaOk
    
    //ACI 318-14 (7.7.2.3)  
    const s_max = Math.min(3 * h, 18)
    
    this.flexureStrengthAndAreaOfSteelStr = `
    <div>Rebar Properties: #${this.steel.rebarSize}, ${d_b} inch diameter, ${this.steel.rebarSpacing} inch spacing, ${numRebar} bars</div>
    <div><em>A<sub>s, design</sub></em> =  ${totalRebarArea.toFixed(2)} in<sup>2</sup></div>
    <div><em>Column Edge to Footing Edge</em> = ${columnEdgeToFootingEdge} inches</div>
    <div><em>M<sub>u</sub> = q<sub>n,u</sub> &times; b<sub>w</sub> &times; Column Edge to Footing Edge <sup>2</sup> / 2</em> = ${Math.round(M_u)} kips-ft</div>
    <div>Assume tension controls: <em>jd = 0.976 &times; d<sub>avg</sub></em> = ${jd} inches</div>
    <div><em>A<sub>s</sub> = M<sub>u</sub> / (&phi; &times; f<sub>y</sub> &times; jd)</em> =  ${As.toFixed(2)} in<sup>2</sup></div>
    <div><em>&beta;<sub>1</sub></em> =  ${beta1}</div>
    <div><em>a = (A<sub>s</sub> &times; fy) / (&beta;<sub>1</sub> &times; f'<sub>c</sub> &times; b<sub>w</sub>)</em> =  ${a.toFixed(2)} in</div>
    <div><em>c = a / &beta;<sub>1</sub></em> =  ${c.toFixed(2)} in</div>
    <div><em>&epsilon;<sub>t</sub> = 0.003 / c &times; d<sub>avg</sub> - 0.003</em> =  ${epsilon_t.toFixed(3)}</div>
    <div>Tension controls if &epsilon;<sub>t</sub> &gt; 0.005</div>
    <div><em>A<sub>s, required</sub> = M<sub>u</sub> / (&phi; &times; f<sub>y</sub> &times; (d<sub>avg</sub> - a/2))</em> =  ${As_final.toFixed(2)} in<sup>2</sup></div>
    <div><em>s<sub>max</sub></em> = ${s_max} in<span class='float-end fw-light'>ACI 318-14 7.7.2.3</span></div>

    `
  }
    
  showFlexureStrengthAndAreaOfSteel() {
    this.configureCard('flexure-strength-check', 'flexure-strength-card', this.flexureOk)
    document.getElementById('flexure-strength-and-area-of-steel').innerHTML = this.flexureStrengthAndAreaOfSteelStr
  }

  calculateReinforcementBarDevelopment() {
    const d_b = rebarProperties[this.steel.rebarSize].d
    const cover = this.footingConditions.cover // how much concrete covers the rebar (inches)
    const fy = this.footingConditions.tensileStrength * 1000 // psi
    const lambda = 1 // ACI 318-19 (Table 25.4.2.4) normal weight concrete 
    const fc = this.footingConditions.concreteStrength * 1000 // psi
    const b_w = this.geometry.footingWidth

    const clearSpacing = this.steel.rebarSpacing - d_b
    const clearSpacingOk = clearSpacing > 2 * d_b
    const clearCoverOk = cover > d_b

    const developmentConstant = this.steel.rebarSize < 7 ? 25 : 20
    const psi_t = 1.0 //ACI 318-19 (Table 25.4.2.4) 
    const psi_e = 1.0 //ACI 318-19 (Table 25.4.2.4) 
    const psi_g = 1.0 //ACI 318-19 (Table 25.4.2.4) 
    let l_d = fy * psi_t * psi_g * psi_e / developmentConstant / lambda / Math.sqrt(fc) * d_b
    let l_d_provided = b_w / 2 - this.geometry.columnWidth / 2 - cover // ACI 318-19 (Table 25.4.2.2)
    const barDevelopementLengthOk = l_d_provided >= l_d

    this.barDevelopementLengthOk = barDevelopementLengthOk && clearSpacingOk && clearCoverOk

    this.reinforcementBarDevelopmentStr = `
    <div><strong>Clear Spacing ${clearSpacingOk ? 'Ok' : 'Failed'}, Clear Cover ${clearCoverOk ? 'Ok' : 'Failed'}</strong></div>
    <div>Rebar Properties: #${this.steel.rebarSize}, ${d_b} inch diameter, ${this.steel.rebarSpacing} inch spacing</div>
    <div>Clear Spacing Between Bars: <em>Rebar Spacing - Rebar Diameter</em> = ${clearSpacing} inches > <em>2 &times; Rebar Diameter</em></div>
    <div>Clear Cover: ${cover} inches &gt; <em>Rebar Diameter</em></div>
    <div>Development Constant: ${developmentConstant} (#6 and smaller = 25, #7 and up = 20)</div>
    <div><em>&lambda;</em> =  1.0 <span class='float-end fw-light'>ACI 318-19 Table 25.4.2.4</span></div>
    <div><em>&psi;<sub>t</sub> = &psi;<sub>e</sub> = &psi;<sub>g</sub></em> = 1.0 <span class='float-end fw-light'>ACI 318-19 Table 25.4.2.4</span></div>
    <div><em>l<sub>d, required</sub> = 1 / ${developmentConstant} &times; f<sub>y</sub> / (&lambda; &times; sqrt(f'<sub>c</sub>)) &times; &psi;<sub>t</sub> &times; &psi;<sub>e</sub> &times; &psi;<sub>g</sub></em> = ${Math.round(l_d)} inches<span class='float-end fw-light'>ACI 318-19 Table 25.4.2.2</span></div>
    <div><em>l<sub>d, design</sub> = b<sub>w</sub> / 2 - Column Width / 2 - Rebar Cover</em> = ${l_d_provided} inches</div>
    `
  }
    
  showReinforcementBarDevelopment() {
    this.configureCard('rebar-development-check', 'rebar-development-card', this.barDevelopementLengthOk)
    document.getElementById('reinforcement-bar-development').innerHTML = this.reinforcementBarDevelopmentStr
  }

  calculateMaxBearingLoad() {
    const b_w = this.geometry.footingWidth
    const fc = this.footingConditions.concreteStrength * 1000 // psi
    const P_u = this.P_u
    const footingWidth = this.geometry.footingWidth
    const columnWidth = this.geometry.columnWidth
    const footingThickness = this.geometry.footingThickness
    let edgeToColumn = footingWidth * 0.5 - columnWidth * 0.5
    const phiMomentReduction = this.footingConditions.flexureStrengthReductionFactor //ACI 318-19 (Table 21.2.1) 

    const A1 = Math.pow(columnWidth, 2)
    const slope = -1/2
    const dx1 = edgeToColumn
    //let dy1 = dx1 * slope
    const dy2 = -footingThickness
    const dx2 = dy2 / slope

    const dx = Math.min(dx1, dx2)

    const d_A2 = dx * 2 + columnWidth 

    //const A2 = Math.pow(b_w, 2)
    const A2 = Math.pow(d_A2, 2)
    // ACI 318-19 (Table 22.8.3.2) 
    const phiB_n_footing_1 = phiMomentReduction * 0.85 * fc * A1 * Math.sqrt(A2/A1) / 1000
    const phiB_n_footing_2 = phiMomentReduction * 0.85 * fc * A1 * 2 / 1000
    const phiB_n_footing = Math.min(phiB_n_footing_1, phiB_n_footing_2) 
    const bearingLoadTopOfFootingOk = phiB_n_footing > P_u

    this.maxBearingLoadOk = bearingLoadTopOfFootingOk
    
    this.maxBearingLoadStr = `
    <div><em>A<sub>1</sub> = ${A1.toFixed(2)} in<sup>2</sup>, A<sub>2</sub> = ${A2.toFixed(2)} in<sup>2</sup> </em><span class='float-end fw-light'>ACI 318-19 Table 22.8.3.2</span></div>
    <div><em>&phi;B<sub>n,footing, 1</sub> = &phi; &times; 0.85 &times; f'<sub>c</sub> &times; A<sub>1</sub> &times sqrt(A<sub>1</sub> / A<sub>2</sub>)</em> =  ${phiB_n_footing_1.toFixed(2)} kips</div>
    <div><em>&phi;B<sub>n,footing, 2</sub> = &phi; &times; 0.85 &times; f'<sub>c</sub> &times; A<sub>1</sub> &times 2</em> =  ${phiB_n_footing_2.toFixed(2)} kips</div>
    <div><em>&phi;B<sub>n,footing</sub> = min(&phi;B<sub>n,footing, 1</sub>, &phi;B<sub>n,footing, 2</sub>)</em> =  ${phiB_n_footing.toFixed(2)} kips</div>
    `
  }
    
  showMaxBearingLoad() {
    this.configureCard('max-bearing-load-check', 'max-bearing-load-card', this.maxBearingLoadOk)
    document.getElementById('max-bearing-load').innerHTML = this.maxBearingLoadStr
  }

  calculateAllowableBearingLoad() {
    const phiMomentReduction = this.footingConditions.flexureStrengthReductionFactor //ACI 318-19 (Table 21.2.1) 
    const fc = this.footingConditions.concreteStrength * 1000 // psi
    const P_u = this.P_u
    const A1 = Math.pow(this.geometry.columnWidth, 2)
    const fy = this.footingConditions.tensileStrength * 1000 // psi

    //ACI 318-14 (Table 22.8.3.2(c)) 
    const phiB_n_column = phiMomentReduction * 0.85 * fc * A1 / 1000 // kips
    const dowelsNeeded = phiB_n_column < P_u
    const As_dowels_1 = (P_u - phiB_n_column) / phiMomentReduction / fy * 1000  
    const As_dowels_2 = 0.005 * Math.pow(this.geometry.columnWidth, 2)
    const As_dowels = Math.max(As_dowels_1, As_dowels_2)
    const allowableBearingLoadOk = true // phiB_n_column < P_u

    this.dowelsNeeded = dowelsNeeded
    this.allowableBearingLoadOk = allowableBearingLoadOk
    
    this.allowableBearingLoadStr = `
    <div><em>&phi;B<sub>n, column</sub> = &phi; &times; 0.85 &times; f'<sub>c</sub> &times; A<sub>1</sub></em> =  ${phiB_n_column.toFixed(2)} kips <span class='float-end fw-light'>ACI 318-19 Table 22.8.3.2(c)</span></div>
    <div>Compression Controlled Column Ties <em>&phi;</em> = 0.65 <span class='float-end fw-light'>ACI 318-14 Table 21.2.2</span></div>
    <div><em>A<sub>s,dowels, 1, required</sub> = (P<sub>u</sub> - &phi; &times; B<sub>n, column</sub>) / (&phi; &times; f'<sub>c</sub>)</em> =  ${As_dowels_1.toFixed(2)} in<sup>2</sup></div>
    <div><em>A<sub>s,dowels, 2, required</sub> = 0.005 &times; A<sub>g</sub></em> =  ${As_dowels_2.toFixed(2)} in<sup>2</sup></div>
    <div><em>A<sub>s,dowels, required</sub> = max(<em>A<sub>s,dowels, 1, required</sub>, <em>A<sub>s,dowels, 2, required</sub>)</em> = ${As_dowels.toFixed(2)} in<sup>2</sup></div>
    `
  }
    
  showAllowableBearingLoad() {
    this.configureCard('allowable-bearing-load-check', 'allowable-bearing-load-card', this.allowableBearingLoadOk)
    document.getElementById('dowels-required').innerHTML = this.dowelsNeeded ? '(Dowels Required)' : ''
    document.getElementById('allowable-bearing-load').innerHTML = this.allowableBearingLoadStr
  }



  updateDesign() {

    this.calculateLoadCombinations()
    this.calculateFootingPressure()
    this.calculateOneWayShear()
    this.calculateTwoWayShear()
    this.calculateFlexureStrengthAndAreaOfSteel()
    this.calculateReinforcementBarDevelopment()
    this.calculateMaxBearingLoad()
    this.calculateAllowableBearingLoad()
    
    if(this.isDesignOk()) {
      //top-design-summary-card
      document.getElementById('top-design-summary-card').classList.add('card-success')
      document.getElementById('top-design-summary-card').classList.remove('card-danger')
      document.getElementById('design-summary-card').classList.add('card-success')
      document.getElementById('design-summary-card').classList.remove('card-danger')
      document.getElementById('design-status').innerHTML = '<i class="fa fa-check fs-5"></i> Design OK'
    } else {
      document.getElementById('top-design-summary-card').classList.remove('card-success')
      document.getElementById('top-design-summary-card').classList.add('card-danger')
      document.getElementById('design-summary-card').classList.remove('card-success')
      document.getElementById('design-summary-card').classList.add('card-danger')
      document.getElementById('design-status').innerHTML = '<i class="fa fa-close fs-5"></i> Design Failed'
    }

    this.showLoadCombinations()
    this.showFootingPressure()
    this.showOneWayShear()
    this.showTwoWayShear()
    this.showFlexureStrengthAndAreaOfSteel()
    this.showReinforcementBarDevelopment()
    this.showMaxBearingLoad()
    this.showAllowableBearingLoad()
  }
}
